import {
    Flex,
    Divider,
    Text,
    VStack,
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from "@chakra-ui/react";
import { usePayment } from "@/hooks/usePayment";
import { formatCurrency, formatDate } from "@/utils";
import { DownloadIcon } from "@/styles/icons";
import { Button, Drawer } from "barte-design-system";

interface DrawerNewPaymentBatchProps {
    isOpen: boolean;
    onClose: () => void;
}

export function DrawerDetailsPaymentBatch({
    isOpen,
    onClose,
}: DrawerNewPaymentBatchProps) {
    const { paymentBatchDetail } = usePayment();

    const batchDetail = paymentBatchDetail;

    const handleCloseDrawer = () => {
        onClose();
    };

    const handleDownload = () => {
        if (batchDetail?.fileUrl) {
            window.location.assign(batchDetail.fileUrl);
        }
    };

    return (
        <Drawer
            title="Detalhes do lote"
            isOpen={isOpen}
            onClose={handleCloseDrawer}
            footer={{ primaryButton: { children: "Fechar", variant: "outline", onClick: handleCloseDrawer } }}
        >
                    <Flex
                        direction="column"
                        bg="white"
                        borderRadius="lg"
                        w="100%"
                        h="100%"
                        p={2}
                        overflowY="auto"
                    >
                        {batchDetail ? (
                            <VStack 
                                p={4}
                                align="stretch" 
                                spacing={4} 
                                w="100%"
                            >
                                {/* Seção de Resumo do Lote */}
                                <VStack 
                                    align="stretch" 
                                    spacing={4} 
                                    w="100%"
                                    border="1px solid #E6E9F0" 
                                    borderRadius="8px" 
                                >
                                    {/* Data de cadastro */}
                                    <Flex 
                                        w="100%"
                                        borderBottom="1px solid #E6E9F0"
                                    >
                                        <Flex
                                            w="100%"
                                            justify="space-between" 
                                            align="center" 
                                            p={4} 
                                        >
                                            <Text fontSize="14px" color="#000" fontWeight="400">
                                                Data de cadastro
                                            </Text>
                                            <Text fontSize="14px" color="#000" fontWeight="600">
                                                {formatDate(batchDetail.batchUploadDate.split("T")[0])}
                                            </Text>
                                        </Flex>
                                    </Flex>

                                    {/* Nome do arquivo */}
                                    <Flex 
                                        w="100%"
                                        borderBottom="1px solid #E6E9F0"
                                    >
                                        <Flex
                                            w="100%"
                                            justify="space-between" 
                                            align="center" 
                                            p={4} 
                                        >
                                            <Flex direction="column" flex="1">
                                                <Text fontSize="14px" color="#000" fontWeight="400" mb={1}>
                                                    Nome do arquivo
                                                </Text>
                                                <Flex 
                                                    w="100%" 
                                                    justify="space-between" 
                                                    align="center" 
                                                    gap={2}
                                                >
                                                    <Text fontSize="14px" color="#000" fontWeight="600">
                                                        {batchDetail.filename}
                                                    </Text>
                                                    
                                                    <Button
                                                        variant="outline"
                                                        iconLeft={<DownloadIcon />}
                                                        onClick={handleDownload}
                                                    >
                                                        Baixar
                                                    </Button>
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    </Flex>

                                    {/* Nome do lote */}
                                    <Flex 
                                        w="100%"
                                        borderBottom="1px solid #E6E9F0"
                                    >
                                        <Flex
                                            w="100%"
                                            justify="space-between" 
                                            align="center" 
                                            p={4} 
                                        >
                                            <Text fontSize="14px" color="#000" fontWeight="400">
                                                Nome do lote
                                            </Text>
                                            <Text fontSize="14px" color="#000" fontWeight="600">
                                                {batchDetail.name}
                                            </Text>
                                        </Flex>
                                    </Flex>

                                    {/* Itens processados */}
                                    <Flex 
                                        w="100%"
                                        borderBottom="1px solid #E6E9F0"
                                    >
                                        <Flex
                                            w="100%"
                                            justify="space-between" 
                                            align="center" 
                                            p={4} 
                                        >
                                            <Text fontSize="14px" color="#000" fontWeight="400">
                                                Itens processados
                                            </Text>
                                            <Text fontSize="14px" color="#000" fontWeight="600">
                                                {batchDetail.numPaymentsTotal || batchDetail.numPaymentsSuccess}
                                            </Text>
                                        </Flex>
                                    </Flex>

                                    {/* Itens processados */}
                                    <Flex 
                                        w="100%"
                                        borderBottom="1px solid #E6E9F0"
                                    >
                                        <Flex
                                            w="100%"
                                            justify="space-between" 
                                            align="center" 
                                            p={4} 
                                        >
                                            <Text fontSize="14px" color="#000" fontWeight="400">
                                                Itens processados com sucesso
                                            </Text>
                                            <Text fontSize="14px" color="#000" fontWeight="600">
                                                {batchDetail.numPaymentsSuccess}
                                            </Text>
                                        </Flex>
                                    </Flex>

                                    {/* Valor total */}
                                    <Flex 
                                        justify="space-between" 
                                        align="center" 
                                        p={4}
                                    >
                                        <Text fontSize="14px" color="#000" fontWeight="400">
                                            Valor total
                                        </Text>
                                        <Text fontSize="14px" color="#000" fontWeight="600">
                                            {formatCurrency({ value: batchDetail.paymentsTotalAmount })}
                                        </Text>
                                    </Flex>
                                </VStack>

                                {/* Tabela de Transações */}
                                {batchDetail.paymentTransactions && batchDetail.paymentTransactions.length > 0 && (
                                    <Box w="100%">
                                        <Table variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th border="none" color="#8A94A8" fontSize="14px" fontWeight="400" px={0} pb={2}>
                                                        Conta
                                                    </Th>
                                                    <Th border="none" color="#8A94A8" fontSize="14px" fontWeight="400" px={0} pb={2}>
                                                        Pagamento
                                                    </Th>
                                                    <Th border="none" color="#8A94A8" fontSize="14px" fontWeight="400" px={0} pb={2} isNumeric>
                                                        Valor
                                                    </Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {batchDetail.paymentTransactions.map((transaction, index) => (
                                                    <Tr key={transaction.id || index} borderBottom="1px solid #E6E9F0">
                                                        <Td border="none" px={0} py={3}>
                                                            <Flex direction="column">
                                                                <Text fontSize="14px" color="#000" fontWeight="500">
                                                                    {transaction.recipientName || "N/A"}
                                                                </Text>
                                                                <Text fontSize="12px" color="#8A94A8">
                                                                    {transaction.costCenter || transaction.recipientDocument || "Centro de custo"}
                                                                </Text>
                                                            </Flex>
                                                        </Td>
                                                        <Td border="none" px={0} py={3}>
                                                            <Text fontSize="14px" color="#000">
                                                                {transaction.scheduledDate 
                                                                    ? new Date(`${transaction.scheduledDate}T00:00:00`).toLocaleDateString("pt-BR", {
                                                                        day: "2-digit",
                                                                        month: "2-digit",
                                                                        year: "2-digit"
                                                                    })
                                                                    : "N/A"
                                                                }
                                                            </Text>
                                                        </Td>
                                                        <Td border="none" px={0} py={3} isNumeric>
                                                            <Text fontSize="14px" color="#000" fontWeight="500">
                                                                {formatCurrency({ value: transaction.amount })}
                                                            </Text>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                )}
                            </VStack>
                        ) : (
                            <Flex justify="center" align="center" h="100%">
                                <Text color="gray.500">
                                    Nenhum dado disponível
                                </Text>
                            </Flex>
                        )}
                    </Flex>
        </Drawer>
    );
}
